import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { SRStatus } from '@contracts/sr';
import { Banner, Empty, Panel } from '../components/Panel';
import { api } from '../lib/api';

function Rule({ n, title, children }: { n: number | string; title: string; children: ReactNode }) {
  return (
    <li className="guide-rule">
      <span className="guide-rule__n" aria-hidden="true">
        {n}
      </span>
      <div>
        <strong>{title}</strong>
        <div className="small">{children}</div>
      </div>
    </li>
  );
}

function Yes({ on }: { on: boolean | undefined }) {
  return on ? <span>✓</span> : <span className="muted">—</span>;
}

/** Guide of the separate support/resistance alert system, with the settings in use. */
export function SRGuide() {
  const q = useQuery({ queryKey: ['sr-status'], queryFn: () => api.get<SRStatus>('/api/v1/sr/status') });
  if (q.isLoading) return <Empty title="กำลังโหลด…" />;
  if (q.isError || !q.data) return <Empty title="โหลดค่ากฎไม่สำเร็จ">ติดต่อ backend ไม่ได้</Empty>;
  const st = q.data;
  const c = st.config;
  const ch = st.channels;
  const lineAll = Boolean(ch?.line && ch.lineAllNotices);
  const hour = c.dailyHour ?? 8;
  const touchStars = c.touchStars ?? 3;
  const updateStars = c.minWatchStars ?? 2;

  return (
    <>
      <p className="page__lede">
        ระบบแจ้งเตือน <strong>แนวรับแนวต้าน + แท่งเทียนกลับตัว</strong> แยกจากระบบ Stochastic ทั้งหมด (หน้า ประวัติ
        และสวิตช์ของตัวเอง) ใช้ราคาจาก MT5 — ค่าที่แสดงในหน้านี้ดึงจากระบบที่ทำงานอยู่จริง
      </p>

      <Banner tone="info" title="แจ้งเตือนเท่านั้น — ระบบไม่ส่งคำสั่งซื้อขาย">
        สัญญาณคือผลของกฎ ไม่ใช่การทำนาย ผลทดสอบย้อนหลังไม่รับประกันผลในอนาคต ไม่ใช่คำแนะนำการลงทุน
      </Banner>

      {!st.enabled ? (
        <Banner tone="warn" title="ระบบนี้ปิดอยู่">
          เปิดได้ด้วย SR_ENABLED=true
        </Banner>
      ) : null}

      <div className="stack" style={{ marginTop: 16 }}>
        <Panel title="1. ภาพรวม">
          <ul className="guide-list">
            <li>
              <strong>ตรวจเมื่อ:</strong> ทุกครั้งที่แท่ง H1 <strong>ปิด</strong> (ใช้เฉพาะแท่งที่ปิดแล้ว) ·
              ติดตามการแตะแนวและผล TP/SL ทุกแท่ง M1
            </li>
            <li>
              <strong>ครั้งละ 1 ไม้:</strong> ระหว่างที่ไม้ก่อนหน้ายังไม่ถึง TP หรือ SL สัญญาณใหม่จะถูกข้าม
              (บันทึกไว้บนเว็บ)
            </li>
            <li>
              <strong>แยกจากระบบ Stochastic:</strong> ทั้งสองระบบอาจเตือนพร้อมกันได้ และนับไม้ของตัวเองแยกกัน
            </li>
            <li>
              ดูแนววันนี้ สถานะ และประวัติได้ที่หน้า <Link to="/sr">สัญญาณแนวรับแนวต้าน</Link>
            </li>
          </ul>
        </Panel>

        <Panel title="2. แนวรับแนวต้านมาจากไหน">
          <ul className="guide-list">
            <li>
              <strong>Pivot รายวัน:</strong> PP, R1–R3, S1–S3 คิดจาก High/Low/Close ของเมื่อวาน (วันทองตัดรอบ 17:00
              นิวยอร์ก) — คงที่ทั้งวัน
            </li>
            <li>
              <strong>High/Low เมื่อวาน</strong> — คงที่ทั้งวัน
            </li>
            <li>
              <strong>จุดกลับตัว</strong> (swing high/low) บน H1, M15 และ H4 — เพิ่มได้ระหว่างวันเมื่อยืนยันครบ
            </li>
            <li>
              <strong>กรอบราคา M15</strong> ที่ราคาเคยแกว่งออกข้าง และ<strong>เลขกลม</strong>ทุก $25
            </li>
            <li>
              แต่ละแนวขยายเป็น <strong>โซน</strong> ตามความผันผวน โซนที่ซ้อนกันรวมเป็นโซนเดียว ·
              แนวที่ราคาปิดทะลุไปแล้วถูกตัดออก
            </li>
            <li>
              <strong>ดาว ★ (1–3):</strong> จำนวนวิธีที่ชี้มาที่โซนเดียวกัน + จำนวนครั้งที่ราคาเคยแตะแล้วเด้ง ·
              แจ้งเตือน “แตะแนว” เฉพาะแนว {'★'.repeat(touchStars)} ขึ้นไป
            </li>
            <li>
              <strong>คำนวณใหม่ทุกชั่วโมง</strong> (ทุกแท่ง H1 ปิด) · สรุปแสดงฝั่งละไม่เกิน 5 แนว เรียงจากใกล้ราคา
            </li>
          </ul>
        </Panel>

        <Panel title="3. เงื่อนไข BUY / SELL (ตรวจที่แท่ง H1 ปิด)">
          <ol className="guide-rules">
            <Rule n={1} title="เด้งที่แนว">
              <strong>BUY:</strong> แท่งเปิดเหนือแนวรับ ไส้แทงลงมาในโซน แล้ว<strong>ปิดกลับขึ้นเหนือโซน</strong> และเป็น
              แท่งเทียนกลับตัวขาขึ้น (เช่น Hammer, Bullish Engulfing, Piercing) · <strong>SELL:</strong>{' '}
              กลับกันที่แนวต้าน
            </Rule>
            <Rule n={2} title="ทะลุแล้ว retest">
              <strong>SELL:</strong> แท่งปิดทะลุแนวรับลงไปชัดเจน (อย่างน้อย {c.break_clear_atr} × ATR) จากนั้นภายใน{' '}
              {c.retest_bars} แท่ง H1 ราคากลับขึ้นมาทดสอบโซน <strong>แต่ปิดไม่ผ่าน</strong> และเป็นแท่งกลับตัวขาลง ·{' '}
              <strong>BUY:</strong> กลับกันที่แนวต้านที่ถูกทะลุขึ้น · แต่ละการทะลุให้สัญญาณได้ครั้งเดียว
            </Rule>
            <Rule n="⏳" title="WAIT">
              ราคาแค่แตะแนว แตะแล้วปิดไม่กลับ หรือปิดกลับแต่ไม่ใช่แท่งกลับตัว — ยังไม่เข้า (การแตะแนวเฉยๆ
              ทดสอบแล้วไปทางไหนก็ได้พอๆ กัน)
            </Rule>
          </ol>
          <p className="small muted">แท่งกลับตัวที่นับ: 14 รูปแบบกลับตัวจาก 18 รูปแบบ (ชุดเดียวกับระบบ Stochastic)</p>
        </Panel>

        <Panel title="4. จุดเข้า SL และ TP">
          <div className="table-wrap">
            <table className="ledger">
              <tbody>
                <tr>
                  <th scope="row">ราคาเข้า</th>
                  <td>ราคาตอนแท่ง H1 ปิด: BUY ที่ Ask · SELL ที่ Bid</td>
                </tr>
                <tr>
                  <th scope="row">Take Profit</th>
                  <td>
                    ราคาเข้า ± {c.tp_atr} × ATR(H1) — ตอนนี้ ATR ${st.atr.toFixed(1)} → TP ประมาณ $
                    {((c.tp_atr ?? 0.5) * st.atr).toFixed(1)} (ยืดหดตามความผันผวนของตลาด)
                  </td>
                </tr>
                <tr>
                  <th scope="row">Stop Loss</th>
                  <td>
                    ปลายไส้ของแท่งสัญญาณ (BUY = Low · SELL = High + spread) + เผื่อระยะ ${c.sl_buffer_min} หรือ{' '}
                    {c.sl_buffer_atr} × ATR (ค่าที่มากกว่า) · ห่างจากราคาเข้าอย่างน้อย ${c.sl_min} · ไม่เลื่อนตลอดไม้
                  </td>
                </tr>
                <tr>
                  <th scope="row">จบไม้เมื่อ</th>
                  <td>
                    ถึง <strong>TP</strong> หรือชน <strong>SL</strong> — ไม่มีหมดอายุ · ติดตามด้วยแท่ง M1 (SELL ใช้ Bid
                    + spread) ถ้า TP กับ SL เกิดในนาทีเดียวกันนับว่าชน SL ก่อน
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="5. แจ้งเตือนแต่ละแบบ">
          <div className="table-wrap">
            <table className="ledger">
              <thead>
                <tr>
                  <th>แจ้งเตือน</th>
                  <th>ส่งเมื่อ</th>
                  <th>LINE</th>
                  <th>แอป</th>
                  <th>เว็บ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🧱 สรุปแนวประจำวัน</td>
                  <td>{hour}:00 น. ทุกวันที่ตลาดเปิด — แนวฝั่งละไม่เกิน 5 แนว พร้อม ★ ระยะห่าง และที่มา</td>
                  <td>
                    <Yes on={ch?.line} />
                  </td>
                  <td>
                    <Yes on={ch?.push} />
                  </td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>🔄 อัปเดตแนว</td>
                  <td>
                    หลังสรุปเช้า เมื่อแนว {'★'.repeat(updateStars)} ขึ้นไปเกิดใหม่ ถูกทะลุ หรือถูกตัดออก —
                    พร้อมสรุปแนวทั้งหมดตอนนั้น (ขอบโซนขยับเล็กน้อยไม่นับ)
                  </td>
                  <td>
                    <Yes on={lineAll} />
                  </td>
                  <td>
                    <Yes on={ch?.push} />
                  </td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>📍 แตะแนว (WAIT)</td>
                  <td>
                    ราคาเข้า<strong>แนว {'★'.repeat(touchStars)} ขึ้นไป</strong> —{' '}
                    <strong>ไม่แจ้งซ้ำแนวเดิมติดๆ กัน</strong> (แนวที่อยู่ติดกันนับเป็นแนวเดียว) จนกว่าราคาจะไปแตะแนว{' '}
                    {'★'.repeat(touchStars)} ขึ้นไปแนวอื่นก่อน แล้วกลับมาแตะแนวเดิม
                  </td>
                  <td>
                    <Yes on={lineAll} />
                  </td>
                  <td>
                    <Yes on={ch?.push} />
                  </td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>⏳ WAIT หลังแท่งปิด</td>
                  <td>แท่ง H1 ปิดหลังแตะแนวแต่ยังไม่ยืนยัน พร้อมเหตุผล — ไม่แจ้งเตือน ดูได้ในหน้าเว็บเท่านั้น</td>
                  <td>—</td>
                  <td>—</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>🟢🔴 BUY / SELL</td>
                  <td>แท่ง H1 ปิดผ่านเงื่อนไขข้อ 3 — ราคาเข้า SL TP เหตุผล และกำไร/ขาดทุนที่ 0.01 lot</td>
                  <td>
                    <Yes on={ch?.line} />
                  </td>
                  <td>
                    <Yes on={ch?.push} />
                  </td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>✅❌ ถึง TP / ชน SL</td>
                  <td>ทันทีที่แท่ง M1 แตะราคานั้น</td>
                  <td>
                    <Yes on={ch?.line} />
                  </td>
                  <td>
                    <Yes on={ch?.push} />
                  </td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>⏸ ข้ามสัญญาณ</td>
                  <td>เกิดสัญญาณระหว่างที่มีไม้เปิดอยู่</td>
                  <td>
                    <Yes on={false} />
                  </td>
                  <td>
                    <Yes on={false} />
                  </td>
                  <td>✓</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="small muted">
            ✓ = เปิดอยู่ตอนนี้ · ผู้รับใช้การตั้งค่าเดียวกับระบบ Stochastic เลือกช่องทางได้ที่หน้า{' '}
            <Link to="/settings">Settings</Link> · ไม่มีแจ้งเตือน “เข้าใกล้แนว” (ยกเลิกแล้ว)
          </p>
        </Panel>

        <Panel title="6. ผลทดสอบย้อนหลังและข้อควรระวัง">
          <ul className="guide-list">
            <li>
              ทดสอบ 4 ช่วง ช่วงละ 3 เดือน (ม.ค.–เม.ย. 2025, ก.ค.–ต.ค. 2025, เม.ย.–ก.ค. 2026, ก.ค.–ต.ค. 2026) ครั้งละ 1
              ไม้ หัก spread และค่าคอมมิชชัน
            </li>
            <li>สัญญาณประมาณ 2 ครั้งต่อวัน · ถึง TP 64–71% · กำไรรวมประมาณ +$343 ที่ 0.01 lot · กำไรใน 3 จาก 4 ช่วง</li>
            <li>
              <strong>ได้ผลดีช่วงตลาดผันผวนสูง</strong> (ปี 2026) แต่<strong>ขาดทุนช่วงตลาดเงียบ</strong> (ก.ค.–ต.ค.
              2025) · ขาดทุนสะสมสูงสุดราว $100–160
            </li>
            <li>ไม้ที่เสียมักใหญ่กว่าไม้ที่ได้ประมาณ 2 เท่า — ต้องชนะเกิน ~65% ถึงจะคุ้ม</li>
            <li>
              <strong>ไม่ควรถือต่อเมื่อชน SL:</strong> ย้อนหลังราว 91% ของไม้ที่ชน SL กลับมาถึง TP ได้ แต่อีก ~9%
              ไม่กลับเลยและติดลบหลายร้อยถึงหลายพันดอลลาร์ที่ 0.01 lot
            </li>
            <li>ระบบไม่มีกฎข่าว — ควรดูปฏิทินข่าวเองก่อนตัดสินใจ</li>
          </ul>
        </Panel>
      </div>
    </>
  );
}
